import { Input, InputRef } from "antd";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useMutation } from "react-query";
import _ from "lodash";
import useCounter from "../../../hooks/useCounter";
import { theme } from "../../../theme";
import { alertSuccess, validatePhone } from "../../../shared/utils";
import { imageFade } from "../../animations";
import Explain from "../../atoms/Explain";
import { StatusType } from "../../atoms/Explain/types";
import { InputPropsType } from "../../atoms/Inputs/InputPropsT";
import Label from "../../atoms/Label";
import { PLACEHOLDER } from "../../constants";
import { checkPhoneAuth } from "../../../apis/PwdReset/apis";

import { sendPhoneNumberWithId } from "../../../apis/Register/apis";
import { CheckTokenRequest } from "./apis/types";
import { HttpError } from "../../../network/types";
import Button from "../../atoms/Button";
import useModals from "../../../hooks/useModals";

interface InputsProps extends InputPropsType {
  onChangePhone: (e: string) => void;
  onChangeAuth: (e: string) => void;

  onExplainClick?: () => void;
  onSendPhone: (e: { authTokenId?: string }) => void;
  onVerifyAuth: (e: { accessToken?: string }) => void;
  height?: number;
  phoneValue?: string;
  authNumValue?: string;
  isPassword?: true;
  isNotBordered?: true;
  repairShopAId: string;
  disabled?: boolean;
  additionalBtn?: any;
  maxLength?: number;
}
const PhoneInput = React.forwardRef<InputRef, InputsProps>(
  (
    {
      authNumValue,
      phoneValue,
      children,
      name,
      height,
      onChangePhone,
      onChangeAuth,
      maxLength,
      onExplainClick,
      onSendPhone,
      onVerifyAuth,
      isPassword,
      disabled,
      repairShopAId,
    },
    ref
  ) => {
    const styles = {
      height,
      name,
    };
    const [isSendDisabled, setIsSendDisabled] = useState<boolean>(false);
    const [checkPhoneAuthRequestBody, setCheckPhoneAuthRequestBody] =
      useState<CheckTokenRequest>();
    const [isSendPhone, setSendPhone] = useState(false);
    const [status, setStatus] = useState<{
      aId: { status?: StatusType; explain?: string };
      phone?: { status?: StatusType; explain?: string };
      auth?: { status?: StatusType; explain?: string };
    }>({
      aId: { status: undefined, explain: undefined },
      phone: { status: undefined, explain: undefined },
      auth: { status: undefined, explain: undefined },
    });

    const { getLoading } = useModals();
    const sendPhoneNumberMutation = useMutation(sendPhoneNumberWithId, {
      onSuccess: (res) => {
        setCheckPhoneAuthRequestBody({
          authTokenId: res.authTokenId,
          token: "",
          key: phoneValue || "",
        });

        if (onSendPhone) onSendPhone({ authTokenId: res.authTokenId });
        setSendPhone(true);
        alertSuccess("문자가 전송되었습니다.인증 코드를 확인해주세요.");
        resetCounter();
        startCounter();
      },
      onError: (err: HttpError) => {
        setStatus({
          ...status,
          phone: {
            status: "error",
            explain: err.message,
          },
        });
      },
    });
    const checkPhoneTokenMutation = useMutation(checkPhoneAuth, {
      onSuccess: (res) => {
        setStatus({
          ...status,
          auth: {
            status: "success",
            explain: "인증번호가 확인되었습니다.",
          },
        });

        if (onVerifyAuth) {
          onVerifyAuth({ accessToken: res.accessToken });
        }
        if (onChangeAuth) onChangeAuth("");
      },
      onError: () => {
        setStatus({
          ...status,
          auth: { status: "error", explain: "올바른 인증번호가 아닙니다." },
        });
      },
    });
    const onSend = () => {
      onSendPhone({ authTokenId: "" });
      onVerifyAuth({ accessToken: "" });

      if (validatePhone(phoneValue) === true) {
        setIsSendDisabled(true);
        setStatus({
          ...status,
          auth: { status: undefined, explain: undefined },
          phone: { status: undefined, explain: undefined },
        });
        sendPhoneNumberMutation.mutate({
          key: phoneValue || "",
          repairShopAid: repairShopAId,

          type: "message",
          phoneNumber: phoneValue || "",
        });
      } else
        setStatus({
          ...status,
          phone: { status: "error", explain: "휴대폰 번호를 입력해주세요." },
        });
    };
    const onVerify = () => {
      if (counter <= 0) {
        if (setStatus)
          setStatus({
            ...status,
            auth: { status: "error", explain: "인증시간이 초과되었습니다." },
          });
      } else if (checkPhoneAuthRequestBody)
        if (authNumValue) {
          checkPhoneTokenMutation.mutate({
            ...checkPhoneAuthRequestBody,
            token: authNumValue,
            authTokenId: checkPhoneAuthRequestBody.authTokenId,
          });
        }
    };
    const deboundcedSend = onSend;
    const {
      counter,
      minuteText,
      secondText,

      resetCounter,
      startCounter,
    } = useCounter(180);
    useEffect(() => {
      if (isSendDisabled)
        setTimeout(() => {
          setIsSendDisabled(false);
        }, 2000);
    }, [isSendDisabled]);
    useEffect(() => {
      if (disabled) setSendPhone(false);
    }, [disabled]);
    return (
      <>
        <Label isRequired>휴대폰 번호</Label>
        <InputContainer disabled={disabled}>
          <NotBorderedInputWrapper
            disabled={disabled}
            status={status.phone?.status}
          >
            <NotBorderedInput
              ref={ref}
              type={isPassword && "password"}
              value={phoneValue}
              disabled={disabled}
              placeholder={PLACEHOLDER.PHONE}
              autoComplete="new-password"
              className="notBordered"
              maxLength={maxLength}
              {...styles}
              onBlur={(e) => {
                onChangePhone(e.target.value.replaceAll("-", "").trim());
              }}
              onPressEnter={
                isSendDisabled
                  ? undefined
                  : disabled
                  ? undefined
                  : deboundcedSend
              }
              onChange={(e) => {
                setSendPhone(false);
                onChangePhone(e.target.value);
              }}
              name="phone"
            />

            <StyledBtnWrapper>
              <Button
                onClick={deboundcedSend}
                type="primary"
                size="large"
                ghost
                disabled={disabled || isSendDisabled || getLoading()}
              >
                {isSendPhone ? "재전송" : "인증번호 전송"}
              </Button>
            </StyledBtnWrapper>
          </NotBorderedInputWrapper>
          {status.phone?.explain && status.phone?.explain.length > 0 && (
            <Explain
              explain={status?.phone?.explain}
              onClick={onExplainClick}
              status={status.phone?.status}
            />
          )}
        </InputContainer>

        {isSendPhone && (
          <>
            <Label isRequired>인증번호</Label>
            <SendAuthInputContainer>
              <NotBorderedInputWrapper status={status.auth?.status}>
                <NotBorderedInput
                  ref={ref}
                  autoFocus
                  value={authNumValue}
                  type={isPassword && "password"}
                  disabled={disabled}
                  placeholder="인증번호"
                  autoComplete="new-password"
                  defaultValue={children as string}
                  className="notBordered"
                  maxLength={maxLength}
                  {...styles}
                  onChange={(e) => {
                    onChangeAuth(e.target.value);
                  }}
                  onPressEnter={() => {
                    if (!disabled) onVerify();
                  }}
                />
                {!disabled && (
                  <StyledTimeCounterWrapper disabled={disabled}>
                    {`${minuteText}:${secondText}`}
                  </StyledTimeCounterWrapper>
                )}
                <StyledBtnWrapper disabled={disabled}>
                  <Button
                    disabled={disabled}
                    onClick={() => onVerify()}
                    type="primary"
                    size="large"
                    ghost
                  >
                    인증번호 확인
                  </Button>
                </StyledBtnWrapper>
              </NotBorderedInputWrapper>
              {status.auth?.explain && status.auth?.explain.length > 0 && (
                <Explain
                  explain={status.auth?.explain}
                  onClick={onExplainClick}
                  status={status.auth?.status}
                />
              )}
            </SendAuthInputContainer>
          </>
        )}
      </>
    );
  }
);
export default PhoneInput;
const NotBorderedInputWrapper = styled.div<{
  status?: StatusType;
  disabled?: boolean;
}>`
  position: relative;
  background-color: ${({ disabled }) => disabled && "#f8f8f8;"};
  display: flex;
  flex-wrap: no-wrap;
  border-bottom: 1px solid #d2d2d2;
  box-sizing: border-box;
  :hover {
    outline: none;
    box-shadow: none;
    border-color: none;
  }

  :focus-within {
    outline: none;
    box-shadow: none;

    border-bottom: 1px solid ${theme.colors.mainPrimary3};
    outline-bottom: 1px solid ${theme.colors.mainPrimary3};
  }
  border-color: ${({ status }) => {
    switch (status) {
      case "success":
        return theme.colors.blue2;
      case "error":
        return theme.colors.red3;
      default:
        return theme.colors.gray6;
    }
  }};
`;
const InputContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 60px;
`;
const SendAuthInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 60px;
  animation: ${imageFade} 0.1s backwards linear alternate;
`;
const NotBorderedInput = styled(Input)<{
  height?: number;
  onClick?: any;
  disabled?: boolean;
}>`
  background-color: #fff;
  border: none;
  /* input box color */
  background-color: ${({ disabled }) => disabled && "#f8f8f8"};
  box-sizing: border-box;
  background: none;
  padding: 16px 4px;
  border-radius: 0px;
  width: 100%;
  height: ${({ height }) => (height ? `${height}px` : "56px")};
  cursor: ${({ onClick }) => (onClick ? "pointer" : "text")};

  ${theme.typo.body.r16};
  :disabled {
    background-color: #f8f8f8;
    color: black;
  }
  :-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    box-shadow: 0 0 0 30px white inset !important;
  }
  :-webkit-autofill {
    font-size: inherit;
  }
  &::placeholder {
    ${theme.typo.body.r14};
    color: ${theme.colors.gray3};
  }
  :hover {
    outline: none;
    box-shadow: none;
    border-color: none;
  }
  :focus {
    outline: none;
    box-shadow: none;
  }
`;
const StyledBtnWrapper = styled.div<{ disabled?: boolean }>`
  background-color: ${({ disabled }) => disabled && "#f8f8f8;"};
  flex: 1 1 0;
  right: 0;
  align-items: center;
  display: flex;
  align-items: center;
`;
const StyledTimeCounterWrapper = styled.div<{ disabled?: boolean }>`
  color: ${theme.colors.red3};
  display: flex;
  align-items: center;
  margin-right: 16px;
  ${theme.typo.body.r16};
`;
